// Thin fetch wrapper for Encore-backed endpoints.
//
// Invariants:
//   * Same-origin relative paths only (dev: Vite proxy, prod: Encore
//     `api.static` served on the same origin — see ADR-010, ADR-012).
//   * `credentials: 'include'` so the `citycare_session` HttpOnly cookie
//     rides along. The SameSite=Lax cookie only survives cross-origin
//     fetches when the server echoes appropriate CORS headers, so keep
//     paths relative.
//   * Encore error bodies are `{code, message, details}` JSON — we
//     surface `code` and `message` on `ApiError` and preserve the raw
//     body for callers that need `details`.
//
// 401 handling is deliberately NOT automatic here. Callers (route
// loaders, auth context) decide whether to redirect via
// `buildLoginUrl(...)` or surface the error to the UI.

export class ApiError extends Error {
  readonly status: number
  readonly code: string | undefined
  readonly body: unknown

  constructor(
    status: number,
    message: string,
    code: string | undefined,
    body: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.body = body
  }
}

export interface ApiFetchInit extends Omit<RequestInit, 'body'> {
  json?: unknown
  body?: BodyInit
  // Optional AbortSignal threaded through to `fetch`. Callers that
  // need request cancellation (debounced inputs, superseded
  // refetches — see issue #42, build spec N13) pass a signal here;
  // omitting it preserves the previous behaviour. When the signal
  // aborts, `fetch` rejects with a DOMException whose `name` is
  // `'AbortError'`; use `isAbortError(err)` to detect and swallow.
  signal?: AbortSignal
}

export async function apiFetch<T = unknown>(
  path: string,
  init: ApiFetchInit = {},
): Promise<T> {
  const { json, body, headers, signal, ...rest } = init
  const finalHeaders = new Headers(headers)
  if (!finalHeaders.has('Accept')) {
    finalHeaders.set('Accept', 'application/json')
  }

  let finalBody: BodyInit | undefined
  if (json !== undefined) {
    if (!finalHeaders.has('Content-Type')) {
      finalHeaders.set('Content-Type', 'application/json')
    }
    finalBody = JSON.stringify(json)
  } else {
    finalBody = body
  }

  const response = await fetch(path, {
    ...rest,
    headers: finalHeaders,
    body: finalBody,
    credentials: 'include',
    signal,
  })

  if (!response.ok) {
    const errBody = await parseJsonSafe(response)
    const message =
      extractString(errBody, 'message') ??
      `Request failed with status ${response.status}`
    const code = extractString(errBody, 'code')
    throw new ApiError(response.status, message, code, errBody)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return (await response.json()) as T
}

// Sentinel so callers can detect+swallow cancellations without
// reaching for DOMException name-matching at every call site.
// Works for both the DOMException thrown by `fetch` on abort and
// any future ApiError we may tag with an AbortError code. Kept
// intentionally narrow — if you find yourself broadening this,
// ask whether the caller actually wants to swallow the error.
export function isAbortError(err: unknown): boolean {
  if (err === null || typeof err !== 'object') return false
  const name = (err as { name?: unknown }).name
  return name === 'AbortError'
}

// Sentinel for 403 Forbidden responses from admin write endpoints.
// Admin pages bubble these to the layout-level ForbiddenBanner via
// useForbidden().reportForbidden() so the user sees a unified
// re-auth prompt instead of a scattered per-form error.
export function isForbiddenError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403
}

// Sentinel for 404 Not Found. True for BOTH an Encore application not_found
// (`code: 'not_found'`, HTTP 404) AND a CODE-LESS 404 — a proxy / CDN / gateway /
// load-balancer in front of Encore that serves its OWN 404 with an HTML/empty
// body, so `apiFetch` surfaces status 404 with no Encore `code` (H3 / issue 694).
// Encore maps only NotFound to HTTP 404, so "status === 404" is the complete
// not_found test. PowerSync upload dispatchers branch on THIS (not a bare
// `err.code === 'not_found'`) so a code-less 404 takes the SAME inline
// disposition — drain / quarantine-with-remapped-ids / emit dropped — as a
// code-bearing not_found, instead of falling through to the central isolation
// backstop with a generic disposition (lost session-recovery, original-id
// quarantine, no `_emitDropped`). Mirrors the status fallback in
// isPermanentUploadError; the lint:powersync-codeless-404-parity gate forbids a
// dispatcher from testing `err.code === 'not_found'` without this.
//
// A TYPE GUARD (`err is ApiError`) so a caller that branches on it still narrows
// `err` to `ApiError` — the dispatchers pass the narrowed `err` to
// `quarantineUnsyncableCount(err: Error)` / Sentry, exactly as the prior
// `err instanceof ApiError && …` check did.
export function isNotFoundError(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 404
}

export function buildLoginUrl(currentUrl?: string): string {
  const redirectTo = currentUrl ?? currentLocationPath()
  return `/auth/login?redirect_to=${encodeURIComponent(redirectTo)}`
}

// Full-page navigation seam. Callers reach for this (instead of
// touching `window.location` inline) so tests can stub the side
// effect without having to re-define non-configurable location
// properties in jsdom.
export function navigateTo(url: string): void {
  if (typeof window !== 'undefined') {
    window.location.href = url
  }
}

// Top-level POST navigation to /auth/logout. Must be a real browser
// submission — not a fetch — because the backend responds 302 to
// Microsoft Entra's logout endpoint so the IdP session is also
// torn down (apps/backend/src/auth/logout.ts). A fetch() would
// either swallow the redirect or trip CORS against Entra; only a
// top-level form submit lets the browser follow 3xx across origins.
export function submitLogout(): void {
  if (typeof document === 'undefined') return
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = '/auth/logout'
  document.body.appendChild(form)
  form.submit()
}

async function parseJsonSafe(response: Response): Promise<unknown> {
  const text = await response.text()
  if (text === '') return undefined
  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

function extractString(body: unknown, key: string): string | undefined {
  if (typeof body !== 'object' || body === null) return undefined
  const record = body as Record<string, unknown>
  const value = record[key]
  return typeof value === 'string' ? value : undefined
}

function currentLocationPath(): string {
  if (typeof window === 'undefined') return '/'
  return `${window.location.pathname}${window.location.search}`
}
